import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, throwError, timer, EMPTY } from 'rxjs';
import { map, catchError, tap, switchMap, finalize, shareReplay } from 'rxjs/operators';

import {
  User,
  LoginRequest,
  LoginResponse,
  RefreshTokenResponse,
  AuthState,
  TokenInfo,
  ApiError,
  mapApiUserToUser
} from '../models/auth.models';
import { TokenStorageService } from './token-storage.service';
import { MockAuthService } from './mock-auth.service';
import { environment } from '../../../environments/environment';

/**
 * Authentication Service
 * Handles user authentication, token management, and auth state
 * Uses modern Angular patterns with signals and functional approach
 * Updated to work with the actual API
 */
@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly tokenStorage = inject(TokenStorageService);
  private readonly mockAuthService = inject(MockAuthService);

  // Configuration
  private readonly useMockApi = false; // Set to true for development with mock data

  // Auth state management
  private readonly authStateSubject = new BehaviorSubject<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: false,
    error: null
  });

  // Public observables
  public readonly authState$ = this.authStateSubject.asObservable();
  public readonly user$ = this.authState$.pipe(map(state => state.user));
  public readonly isAuthenticated$ = this.authState$.pipe(map(state => state.isAuthenticated));
  public readonly isLoading$ = this.authState$.pipe(map(state => state.isLoading));
  public readonly error$ = this.authState$.pipe(map(state => state.error));

  // API endpoints
  private readonly apiUrl = environment.apiUrl;
  private readonly endpoints = {
    login: `${this.apiUrl}Auth/login`,
    refresh: `${this.apiUrl}Auth/refresh`,
    logout: `${this.apiUrl}Auth/logout`,
    profile: `${this.apiUrl}Auth/profile`
  };

  // Auto-refresh timer
  private refreshTimer?: ReturnType<typeof setTimeout>;

  constructor() {
    this.initializeAuthState();
  }

  /**
   * Initialize authentication state from stored tokens
   */
  private initializeAuthState(): void {
    const user = this.tokenStorage.getUser();
    const isAuthenticated = this.tokenStorage.isAuthenticated();

    if (isAuthenticated && user) {
      this.updateAuthState({ user, isAuthenticated, isLoading: false, error: null });
      this.scheduleTokenRefresh();
    } else {
      this.tokenStorage.clearAll();
    }
  }

  public refreshAuthStateFromStorage(): void {
    this.initializeAuthState();
  }

  public emitCurrentUser(): void {
    const user = this.tokenStorage.getUser();
    if (user) {
      this.updateAuthState({
        ...this.authStateSubject.value,
        user
      });
    }
  }
  

  /**
   * Login user with email and password
   */
  login(credentials: LoginRequest): Observable<LoginResponse> {
    this.updateAuthState({ ...this.authStateSubject.value, isLoading: true, error: null });

    const loginRequest = this.useMockApi
      ? this.mockAuthService.login(credentials)
      : this.http.post<LoginResponse>(this.endpoints.login, credentials);

    return loginRequest.pipe(
      tap(response => {
        if (response.success) {
          this.handleLoginSuccess(response);
        } else {
          throw new Error(response.message || 'Login failed');
        }
      }),
      catchError(error => {
        this.handleAuthError(error);
        return throwError(() => error);
      }),
      finalize(() => {
        this.updateAuthState({ ...this.authStateSubject.value, isLoading: false });
      })
    );
  }

  /**
   * Logout user and clear all auth data
   */
  logout(): Observable<void> {
    this.updateAuthState({ ...this.authStateSubject.value, isLoading: true });

    // Clear refresh timer
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = undefined;
    }

    // Call logout endpoint (optional - continue even if it fails)
    const logoutRequest = this.http.post<void>(this.endpoints.logout, {}).pipe(
      catchError(() => EMPTY) // Ignore logout endpoint errors
    );

    return logoutRequest.pipe(
      finalize(() => {
        this.tokenStorage.clearAll();
        this.updateAuthState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: null
        });
        this.router.navigate(['/sign-in']);
      })
    );
  }

  /**
   * Refresh authentication token
   */
  refreshToken(): Observable<RefreshTokenResponse> {
    const tokenInfo = this.tokenStorage.getTokenInfo();
    if (!tokenInfo?.refreshToken) {
      return throwError(() => new Error('No refresh token available'));
    }

    const refreshRequest = this.useMockApi
      ? this.mockAuthService.refreshToken(tokenInfo.refreshToken)
      : this.http.post<RefreshTokenResponse>(this.endpoints.refresh, {
        refreshToken: tokenInfo.refreshToken
      });

    return refreshRequest.pipe(
      tap(response => {
        if (response.success) {
          this.handleRefreshSuccess(response);
        } else {
          throw new Error(response.message || 'Token refresh failed');
        }
      }),
      catchError(error => {
        this.handleRefreshError(error);
        return throwError(() => error);
      }),
      shareReplay(1)
    );
  }

  /**
   * Get current user profile
   */
  getCurrentUser(): Observable<User> {
    return this.http.get<User>(this.endpoints.profile).pipe(
      tap(user => {
        this.tokenStorage.setUser(user);
        this.updateAuthState({
          ...this.authStateSubject.value,
          user
        });
      }),
      catchError(error => {
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Check if user has specific role
   */
  hasRole(role: string): boolean {
    const user = this.authStateSubject.value.user;
    return user?.roles.includes(role) ?? false;
  }

  /**
   * Check if user has any of the specified roles
   */
  hasAnyRole(roles: string[]): boolean {
    const user = this.authStateSubject.value.user;
    return user?.roles.some(role => roles.includes(role)) ?? false;
  }

  /**
   * Get current authentication state
   */
  getCurrentAuthState(): AuthState {
    return this.authStateSubject.value;
  }

  /**
   * Check if currently authenticated
   */
  isAuthenticated(): boolean {
    return this.tokenStorage.isAuthenticated();
  }

  // Private helper methods
  // private handleLoginSuccess(response: LoginResponse): void {
  //   // Decode JWT to get expiration time
  //   const expiresAt = this.getTokenExpirationTime(response.token);

  //   const tokenInfo: TokenInfo = {
  //     accessToken: response.token,
  //     refreshToken: response.refreshToken,
  //     expiresAt: expiresAt,
  //     tokenType: 'Bearer'
  //   };

  //   // Convert API user to internal User model
  //   const user = mapApiUserToUser(response.user);

  //   this.tokenStorage.setTokenInfo(tokenInfo);
  //   this.tokenStorage.setUser(user);

  //   this.updateAuthState({
  //     user: user,
  //     isAuthenticated: true,
  //     isLoading: false,
  //     error: null
  //   });

  //   this.scheduleTokenRefresh();
  // }
  
// Add this private helper method to handle the API response parsing
private handleLoginSuccess(response: LoginResponse): void {
  console.log('Raw login response:', response);
  
  // Handle roles that might come as JSON string from SQL
  if (response.user && typeof response.user.roles === 'string') {
    try {
      response.user.roles = JSON.parse(response.user.roles);
      console.log('Parsed roles from JSON string:', response.user.roles);
    } catch (e) {
      console.warn('Failed to parse roles JSON, using primary role:', e);
      response.user.roles = [{
        RoleId: response.user.roleId,
        RoleName: response.user.roleName
      }];
    }
  }

  // Decode JWT to get expiration time
  const expiresAt = this.getTokenExpirationTime(response.token);

  const tokenInfo: TokenInfo = {
    accessToken: response.token,
    refreshToken: response.refreshToken,
    expiresAt: expiresAt,
    tokenType: 'Bearer'
  };

  // Convert API user to internal User model
  const user = mapApiUserToUser(response.user);
  
  console.log('Mapped user with roles:', user);

  this.tokenStorage.setTokenInfo(tokenInfo);
  this.tokenStorage.setUser(user);
  if (user.businessId && user.businessId > 0) {
    this.tokenStorage.setActiveVenueId(user.businessId);
  }

  this.updateAuthState({
    user: user,
    isAuthenticated: true,
    isLoading: false,
    error: null
  });

  this.scheduleTokenRefresh();
}
  private handleRefreshSuccess(response: RefreshTokenResponse): void {
    // Decode JWT to get expiration time
    const expiresAt = this.getTokenExpirationTime(response.token);

    const tokenInfo: TokenInfo = {
      accessToken: response.token,
      refreshToken: response.refreshToken,
      expiresAt: expiresAt,
      tokenType: 'Bearer'
    };

    this.tokenStorage.setTokenInfo(tokenInfo);
    this.scheduleTokenRefresh();
  }

  /**
   * Decode JWT token to get expiration time
   */
  private getTokenExpirationTime(token: string): number {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      // JWT exp is in seconds, convert to milliseconds
      return payload.exp ? payload.exp * 1000 : Date.now() + (3600 * 1000); // Default 1 hour
    } catch (error) {
      console.error('Failed to decode token:', error);
      // Default to 1 hour from now if decoding fails
      return Date.now() + (3600 * 1000);
    }
  }

  private handleRefreshError(error: any): void {
    console.error('Token refresh failed:', error);
    this.tokenStorage.clearAll();
    this.updateAuthState({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: 'Session expired. Please login again.'
    });
    this.router.navigate(['/sign-in']);
  }

  private handleAuthError(error: HttpErrorResponse): void {
    let errorMessage = 'An error occurred';

    if (error.error?.message) {
      errorMessage = error.error.message;
    } else if (error.status === 401) {
      errorMessage = 'Invalid credentials';
    } else if (error.status === 0) {
      errorMessage = 'Network error. Please check your connection.';
    }

    this.updateAuthState({
      ...this.authStateSubject.value,
      error: errorMessage
    });
  }

  private updateAuthState(newState: AuthState): void {
    this.authStateSubject.next(newState);
  }

  private scheduleTokenRefresh(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    const tokenInfo = this.tokenStorage.getTokenInfo();
    if (!tokenInfo) return;

    const now = Date.now();
    const expiresAt = tokenInfo.expiresAt;
    const refreshTime = expiresAt - now - (5 * 60 * 1000); // Refresh 5 minutes before expiry

    if (refreshTime > 0) {
      this.refreshTimer = setTimeout(() => {
        this.refreshToken().subscribe({
          error: () => {
            // Refresh failed, will be handled by handleRefreshError
          }
        });
      }, refreshTime);
    }
  }
}
